import { HTMLAttributes, PropsWithChildren } from 'react'
import { NavLink, NavLinkProps } from 'react-router-dom'

import { IconButton, IconButtonProps } from 'components/Button/IconButton'
import { Select, Combobox, SelectProps, ComboboxProps } from 'components/Select'
import { Tooltip } from 'components/Tooltip'
import { sva, cx } from 'styled-system/css'
import { Flex, FlexProps } from 'styled-system/jsx'
import { Button, ButtonProps } from 'ui-kit/button'
import { Icon } from 'ui-kit/icon'
import { IconButton as UiKitIconButton } from 'ui-kit/icon-button'
import { Input, InputProps } from 'ui-kit/input'
import { Text, TextProps } from 'ui-kit/text'
import { createSlotRecipeContext } from 'ui-kit/utils/create-slot-recipe-context'

const controlGroupRecipe = sva({
  className: 'controlGroup',
  slots: ['wrapper', 'button', 'iconLink', 'select', 'text', 'input'],
  base: {
    wrapper: {
      ['--control-group-border-radius']: '{radii.l3}',
      gap: 0,
      alignItems: 'stretch',
      borderColor: 'colorPalette.a5',
      borderWidth: 1,
      borderRadius: 'var(--control-group-border-radius)',
      backgroundColor: 'bg.subtle',
      colorPalette: 'neutral',
    },
    button: {
      // Compensate for the border width
      height: 'calc(var(--control-height) - 2px)',
      flexGrow: 0,
      flexShrink: 0,
      borderRadius: 'calc(var(--control-group-border-radius) - 1px)',
      '&:not(:first-child)': {
        borderLeftRadius: 'none',
        borderColor: 'inherit',
        borderLeftWidth: 1,
      },
      '&:not(:last-child)': {
        borderRightRadius: 'none',
      },
    },
    iconLink: {
      position: 'absolute',
      inset: 0,
    },
    select: {
      '& [data-scope="select"][data-part="trigger"]': {
        // Compensate for the border width
        height: 'calc(var(--control-height) - 2px)',
        textWrap: 'nowrap',
        borderRadius: 'calc(var(--control-group-border-radius) - 1px)',
      },
      '&:not(:first-child)': {
        borderColor: 'inherit',
        borderLeftWidth: 1,
        borderStyle: 'solid',
        '& [data-scope="select"][data-part="trigger"]': {
          borderLeftRadius: 'none',
        },
      },
      '&:not(:last-child)': {
        '& [data-scope="select"][data-part="trigger"]': {
          borderRightRadius: 'none',
        },
      },
    },
    text: {
      alignContent: 'center',
      truncate: true,
      flexGrow: 0,
      flexShrink: 0,
      '&:not(:first-child)': {
        borderColor: 'inherit',
        borderLeftWidth: 1,
      },
    },
    input: {
      // Compensate for the border width
      height: 'calc(var(--control-height) - 2px)',
      '&:not(:hover, :focus)': {
        backgroundColor: 'bg.default',
      },
      '&:not(:first-child)': {
        borderLeftRadius: 'none',
        borderColor: 'inherit',
        borderLeftWidth: 1,
        borderLeftStyle: 'solid',
      },
      '&:not(:last-child)': {
        borderRightRadius: 'none',
      },
    },
  },
  variants: {
    size: {
      dense: {
        wrapper: {
          '--control-height': '{sizes.7}',
        },
        text: {
          paddingInline: '2.5',
        },
      },
      sm: {
        wrapper: {
          '--control-height': '{sizes.9}',
        },
        text: {
          paddingInline: '2.5',
        },
      },
      md: {
        wrapper: {
          '--control-height': '{sizes.10}',
        },
        text: {
          paddingInline: '3',
        },
      },
      lg: {
        wrapper: {
          '--control-height': '{sizes.11}',
        },
        text: {
          paddingInline: '3.5',
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

const context = createSlotRecipeContext(controlGroupRecipe)

const Root = context.withProvider<FlexProps>(Flex, 'wrapper', {
  forwardVariantsToSlots: ['size'],
  props: {
    width: 'fit-content',
  },
})
const ButtonItem = context.withContext<
  ButtonProps & HTMLAttributes<HTMLButtonElement>
>(Button, 'button', {
  variant: 'ghost',
})
const IconButtonItem = context.withContext<IconButtonProps>(
  IconButton,
  'button',
  {
    variant: 'ghost',
  },
)
const SelectItem = context.withContext<SelectProps>(Select, 'select', {
  variant: 'ghost',
})
const ComboboxItem = context.withContext<ComboboxProps>(Combobox, 'select', {
  variant: 'ghost',
})
const InputItem = context.withContext<InputProps>(Input, 'input', {
  variant: 'ghost',
})
const TextItem = context.withContext<TextProps>(Text, 'text', {
  as: 'span',
  textStyle: 'md',
  color: 'fg.muted',
})

function LinkItem({
  children,
  className,
  ...props
}: Omit<NavLinkProps, 'className'> & { className?: string }) {
  const { forwardedProps, slotStyles } = context.use()

  return (
    <Button
      asChild
      variant='ghost'
      {...forwardedProps}
      className={cx(slotStyles.button, className)}
    >
      <NavLink {...props}>{children}</NavLink>
    </Button>
  )
}

function IconLinkItem({
  children,
  tooltip,
  className,
  ...props
}: PropsWithChildren<
  Omit<NavLinkProps, 'className' | 'children'> & {
    className?: string
    tooltip: string
  }
>) {
  const { forwardedProps, slotStyles } = context.use()

  return (
    <Tooltip tooltip={tooltip}>
      <UiKitIconButton
        variant='ghost'
        className={cx(slotStyles.button, className)}
        {...forwardedProps}
      >
        <Text as='span' display='contents'>
          <NavLink className={cx(slotStyles.iconLink)} {...props} />
          <Icon>{children}</Icon>
        </Text>
      </UiKitIconButton>
    </Tooltip>
  )
}

const ControlGroup = {
  Root,
  Button: ButtonItem,
  Select: SelectItem,
  Input: InputItem,
  Combobox: ComboboxItem,
  IconButton: IconButtonItem,
  Text: TextItem,
  Link: LinkItem,
  IconLink: IconLinkItem,
}

export { ControlGroup }
